"use client";

import { Quote } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function IndustryConnections() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: marqueeRef,
    isVisible: marqueeVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const partnersRowOne = [{
    name: "Netflix",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_netflix.png"
  }, {
    name: "HBO",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_hbo.png"
  }, {
    name: "A24",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_a24.png"
  }, {
    name: "National Theatre",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_national.png"
  }, {
    name: "BBC",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_bbc.png"
  }, {
    name: "Paramount",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_paramount.png"
  }, {
    name: "Apple TV",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_apple.png"
  }, {
    name: "Warner Bros",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_warner.png"
  }];
  const partnersRowTwo = [{
    name: "Royal Shakespeare Company",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_rsc.png"
  }, {
    name: "Hulu",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_hulu.png"
  }, {
    name: "Disney",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_disney.png"
  }, {
    name: "Amazon Studios",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_amazon.png"
  }, {
    name: "Old Vic",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_oldvic.png"
  }, {
    name: "Universal",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_universal.png"
  }, {
    name: "Showtime",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_showtime.png"
  }, {
    name: "Sony Pictures",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_sony.png"
  }];
  const testimonials = [{
    quote: "Jordan possesses a rare cinematic gravity. He doesn't just play a role; he commands the frame with absolute stillness.",
    name: "Marcus Thorne",
    role: "Casting Director",
    company: "A24 Studios",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_a24.png"
  }, {
    quote: "His range from classical theater to gritty modern drama is exceptional. A true professional who understands the lens.",
    name: "Elena Vance",
    role: "Executive Producer",
    company: "HBO",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_hbo.png"
  }, {
    quote: "Working with Jordan is a masterclass in preparation. He brings a nuanced depth to every scene we've shot together.",
    name: "Julian Rossi",
    role: "Film Director",
    company: "Netflix",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/studio_netflix.png"
  }];
  return <section data-section-id="879" id="industry-connections" className="relative overflow-hidden bg-background py-24 text-foreground">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      <div className="relative mx-auto max-w-7xl px-6">
        {}
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <h2 className="font-default mt-5 text-4xl font-semibold leading-tight tracking-tight sm:text-5xl uppercase">
            <span style={{
            fontFamily: "var(--font-serif)"
          }}>
              Collaborations with leading{" "}
              <em className="text-primary not-italic">
                <span className="italic">studios</span>
              </em>
              .
            </span>
          </h2>
          <p className="mt-5 text-base font-sans font-light text-muted-foreground">
            Jordan Rivers has established a presence across major networks and prestigious stages. 
            His work is recognized by the industry's most discerning creators and casting houses.
          </p>
        </div>

        {}
        

        {}
        <div ref={cardsRef} className={`mt-20 grid grid-cols-1 gap-6 transition-all duration-700 ease-out md:grid-cols-3 ${cardsVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          {testimonials.map((t, i) => <Card key={i} data-index={i} className="relative rounded-md border border-border bg-card transition-colors hover:border-primary/40">
              <CardContent className="flex h-full flex-col gap-6 p-8">
                <div className="flex items-start justify-between">
                  <Quote className="h-5 w-5 text-primary opacity-50" aria-hidden="true" />
                  <img src={t.logo} alt={t.company} loading="lazy" decoding="async" className="h-5 w-auto opacity-30 grayscale brightness-200" />
                </div>
                <p className="font-sans text-base font-light leading-relaxed text-foreground italic">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="mt-auto border-t border-border pt-4">
                  <p className="font-default text-sm font-semibold tracking-wide text-foreground uppercase">
                    {t.name}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t.role} &middot; {t.company}
                  </p>
                </div>
              </CardContent>
            </Card>)}
        </div>
      </div>

      <style>{`
        @keyframes marquee-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.3333%); }
        }
        @keyframes marquee-right {
          0% { transform: translateX(-33.3333%); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </section>;
}